import { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useNavigate } from "react-router";
import type { WallOfLove } from "../types";
import IconRecycleBinThrow from "@/components/icons/icon-recycle-bin-throw";
import IconCheck from "@/components/icons/icon-check";
import Tooltip from "@/components/ui/tooltip";
import { copyToClipboard } from "@/lib/utils";
import type { UserRole } from "@/modules/membership";

export interface WallOfLoveListItemProps {
	wallOfLove: WallOfLove;
	onDelete?: (id: string) => void;
	onEdit?: (id: string) => void;
	onStatusChange?: (id: string, status: string) => void;
	brandUrl?: string;
	userRole?: UserRole | null;
}

function formatDate(dateString: string) {
	const date = new Date(dateString);
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

function StatusDropdown({ status, onStatusChange, wallOfLoveId }: { status: string; onStatusChange?: (id: string, status: string) => void; wallOfLoveId: string }) {
	const isPublished = status === "published";
	const isUnpublished = status === "unpublished";

	const getStatusColor = () => {
		if (isPublished) return "text-green-700";
		return "text-gray-500";
	};

	const getStatusBgColor = () => {
		if (isPublished) return "bg-green-500";
		return "bg-gray-400";
	};

	const getStatusLabel = () => {
		if (isPublished) return "Published";
		return "Unpublished";
	};

	// If no status change handler, show simple status indicator
	if (!onStatusChange) {
		return (
			<span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md ${getStatusColor()}`}>
				<span className={`w-1.5 h-1.5 rounded-full ${getStatusBgColor()}`} />
				{getStatusLabel()}
			</span>
		);
	}

	return (
		<DropdownMenu.Root>
			<DropdownMenu.Trigger asChild>
				<button type="button" className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md cursor-pointer transition-colors hover:bg-gray-100 ${getStatusColor()}`}>
					<span className={`w-1.5 h-1.5 rounded-full ${getStatusBgColor()}`} />
					{getStatusLabel()}
					<svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
					</svg>
				</button>
			</DropdownMenu.Trigger>
			<DropdownMenu.Portal>
				<DropdownMenu.Content className="bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[120px]" sideOffset={5}>
					<DropdownMenu.Item
						className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer outline-none hover:bg-gray-50"
						onSelect={() => onStatusChange(wallOfLoveId, "published")}
					>
						<span className="text-gray-700">Published</span>
						{isPublished && <IconCheck className="w-4 h-4 text-brand" />}
					</DropdownMenu.Item>
					<DropdownMenu.Item
						className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer outline-none hover:bg-gray-50"
						onSelect={() => onStatusChange(wallOfLoveId, "unpublished")}
					>
						<span className="text-gray-700">Unpublished</span>
						{isUnpublished && <IconCheck className="w-4 h-4 text-brand" />}
					</DropdownMenu.Item>
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);
}

export default function WallOfLoveListItem({ wallOfLove, onDelete, onEdit, onStatusChange, brandUrl, userRole }: WallOfLoveListItemProps) {
	const { id, status, created_at, slug, title, type, blocked_by_plan } = wallOfLove;
	const [copied, setCopied] = useState(false);
	const navigate = useNavigate();
	const isAdmin = userRole === "admin";

	// Generate the wall of love URL
	const wallOfLoveUrl = `${import.meta.env.VITE_CLIENT_WALLS_URL}/w/${slug}`;

	const handleCopyLink = async () => {
		try {
			await copyToClipboard(wallOfLoveUrl);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error("Failed to copy:", err);
		}
	};

	const displayTitle = title || "Untitled Wall of Love";
	const getTypeLabel = () => {
		return type === "dynamic" ? "Dynamic" : "Static";
	};
	const getTypeColor = () => {
		return type === "dynamic" ? "text-blue-700 bg-blue-50" : "text-purple-700 bg-purple-50";
	};

	return (
		<div className={`flex items-center justify-between p-4 rounded-xl border transition-all ${blocked_by_plan ? "bg-gray-50 border-gray-200" : "bg-white border-gray-200 hover:shadow-sm hover:border-gray-300"}`}>
			{/* Left: Title and metadata */}
			<div className={`flex items-center gap-4 min-w-0 flex-1 ${blocked_by_plan ? "opacity-75" : ""}`}>
				{/* Wall of Love icon */}
				<div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center flex-shrink-0">
					<svg className="w-5 h-5 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={1.5}
							d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
						/>
					</svg>
				</div>

				{/* Title and meta */}
				<div className="min-w-0">
					<h3 className="text-sm font-medium text-primary truncate">{displayTitle}</h3>
					<div className="flex items-center gap-3 mt-1">
						<span className="text-xs text-tertiary">Created {formatDate(created_at)}</span>
						<span className="text-xs text-tertiary">•</span>
						<span className={`inline-flex items-center justify-center w-16 py-0.5 text-xs font-medium rounded ${getTypeColor()}`}>{getTypeLabel()}</span>
						{/* <span className="text-xs text-tertiary">•</span> */}
						{/* <span className="text-xs text-tertiary">0 views</span> */}
					</div>
				</div>
			</div>

			{/* Right: Actions */}
			<div className="flex items-center gap-1">
			{blocked_by_plan ? (
				<>
					{/* Status indicator (disabled) for blocked walls */}
					<span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md text-gray-400">
						<span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
						Disabled
					</span>

					{/* Divider */}
					<div className="w-px h-5 bg-gray-200 mx-1" />

					{/* Bolt icon with tooltip for blocked walls - clickable for admins */}
					{isAdmin && brandUrl ? (
						<Tooltip content="This wall is not available for your current plan. Click to upgrade.">
							<button
								type="button"
								onClick={() => navigate(`/${brandUrl}/billing`)}
								className="p-1 rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
							>
								<svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
									<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
								</svg>
							</button>
						</Tooltip>
					) : (
						<Tooltip content="This wall is not available for your current plan. Please upgrade to enable it.">
							<span className="p-1">
								<svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
									<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
								</svg>
							</span>
						</Tooltip>
					)}

					{/* Divider */}
					<div className="w-px h-5 bg-gray-200 mx-1" />
				</>
				) : (
					<>
						{/* Status dropdown */}
						<StatusDropdown status={status} onStatusChange={onStatusChange} wallOfLoveId={id} />

						{/* Divider */}
						<div className="w-px h-5 bg-gray-200 mx-1" />

						{/* Copy link button */}
						{status === "unpublished" ? (
							<Tooltip content="Link is only available when the wall is published">
								<span>
									<button
										type="button"
										disabled
										className="flex items-center justify-center gap-1.5 w-24 py-1.5 text-xs font-medium rounded-md transition-colors text-gray-300 cursor-not-allowed"
									>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={1.5}
												d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
											/>
										</svg>
										<span>Copy Link</span>
									</button>
									</span>
								</Tooltip>
							) : (
								<button
									type="button"
									onClick={handleCopyLink}
									className="flex items-center justify-center gap-1.5 w-24 py-1.5 text-xs font-medium rounded-md transition-colors text-tertiary hover:text-primary hover:bg-gray-100"
								>
									{copied ? (
										<>
											<svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
											</svg>
											<span className="text-green-600">Copied!</span>
										</>
										) : (
										<>
											<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={1.5}
													d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
												/>
											</svg>
											<span>Copy Link</span>
										</>
										)}
									</button>
								)}

						{/* Edit button */}
						{onEdit && (
							<button
								type="button"
								onClick={() => onEdit(id)}
								className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-tertiary hover:text-primary hover:bg-gray-100 rounded-md transition-colors"
							>
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
									/>
								</svg>
								<span>Edit</span>
							</button>
						)}
					</>
				)}

				{/* Delete button - always available */}
				{onDelete && (
					<button type="button" onClick={() => onDelete(id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" aria-label="Delete wall of love">
						<IconRecycleBinThrow className="w-4 h-4" />
					</button>
				)}
			</div>
		</div>
	);
}
